import React, { ReactElement } from "react"
import { offset, Placement } from "@floating-ui/react"
import {
  Box, CloseButton,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup, InputRightElement,
  Portal,
  Text,
} from "@chakra-ui/react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "components/inputs/DatePickerField.css"
import { toZonedTime, fromZonedTime } from "date-fns-tz"
import { useCurrentEvent } from "queries/events"
import {
  Control, ControllerRenderProps, FieldValues, useController,
} from "react-hook-form"
import {
  endOfDay, isSameDay, isValid, startOfDay,
} from "date-fns"
import { DateFormat } from "utilities/constants"

type Props<T extends FieldValues> = {
  name: string;
  control: Control<T, object>;
  label?: string;
  placeholder?: string;
  vertical?: boolean;
  utc?: boolean;
  showTimeSelect?: boolean;
  variant?: string;
  disabled?: boolean;
  minDate?: Date;
  helperText?: string;
  showDeleteButton?: boolean
  inline?: boolean
  open?: boolean
  shouldCloseOnSelect?: boolean
  customInput?: ReactElement
  calendarOffset?: number
  popperPlacement?: Placement
  renderInPortal?: boolean
  onCalendarClose?: () => void
  intervalStartDate?: string
  intervalEndDate?: string
  selectsStart?: boolean
  selectsEnd?: boolean
  error?: boolean
  errorText?: string
}

const DatePickerField = <T extends FieldValues, >({
  name,
  control,
  label,
  placeholder,
  vertical,
  utc,
  showTimeSelect,
  variant = "flushed",
  disabled,
  minDate,
  helperText,
  showDeleteButton = false,
  inline = false,
  open,
  shouldCloseOnSelect,
  customInput,
  calendarOffset = 0,
  popperPlacement,
  renderInPortal,
  onCalendarClose,
  intervalStartDate,
  intervalEndDate,
  selectsStart,
  selectsEnd,
  error,
  errorText,
}: Props<T>) => {
  const {
    data: {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  } = useCurrentEvent()
  const { field } = useController<any>({ name, control })

  const utcToZonedUtility = (date: Date | undefined) => (date ? toZonedTime(new Date(date), utc ? "UTC" : timezone) : undefined)
  const intervalStartDateInDateFormat = intervalStartDate ? new Date(intervalStartDate) : undefined
  const intervalEndDateInDateFormat = intervalEndDate ? new Date(intervalEndDate) : undefined

  let selected: Date | null = null
  if (field.value && isValid(new Date(field.value))) {
    selected = utcToZonedUtility(field.value) as Date
  }

  const calculateMinMaxTime = () => {
    let minTime

    // When there's no intervas provided we shouldn't set the min and max time
    if (!intervalStartDateInDateFormat || !intervalEndDateInDateFormat) {
      return { minTime: undefined, maxTime: undefined }
    }

    // If the same day was selected we should restrict the minTime based on the startDate's time
    // (only when we have the minDate also set so we're using the endTime picker currently)
    if (isSameDay(utcToZonedUtility(intervalStartDateInDateFormat)!,
      utcToZonedUtility(intervalEndDateInDateFormat)!) && minDate) {
      minTime = utcToZonedUtility(intervalStartDateInDateFormat)
    } else {
      minTime = startOfDay(new Date())
    }

    return { minTime, maxTime: endOfDay(new Date()) }
  }

  const { minTime, maxTime } = calculateMinMaxTime()

  return (
    <FormControl
      mb={4}
      display="flex"
      alignItems="flex-start"
      flexDirection={vertical ? "column" : "row"}
      isDisabled={disabled}
      isInvalid={error}
    >
      {label && (
        <FormLabel
          w={vertical ? "full" : "25%"}
          fontSize="md"
          mt={vertical ? 0 : 2}
        >
          {label}
        </FormLabel>
      )}
      <Box w="100%">
        <DatePicker
          timeIntervals={15}
          selectsStart={selectsStart}
          selectsEnd={selectsEnd}
          startDate={intervalStartDateInDateFormat}
          endDate={intervalEndDateInDateFormat}
          inline={inline}
          open={open}
          shouldCloseOnSelect={shouldCloseOnSelect}
          showTimeSelect={showTimeSelect}
          showPopperArrow={false}
          selected={selected}
          onChange={(date) => {
            field.onChange(date ? fromZonedTime(date, utc ? "UTC" : timezone) : null)
          }}
          onCalendarClose={() => {
            field.onBlur()
            onCalendarClose?.()
          }}
          dateFormat={showTimeSelect ? "MMMM d, yyyy h:mm aa" : DateFormat.MONTH_DAY_YEAR}
          placeholderText={placeholder}
          customInput={customInput || (
          <CustomInput
            variant={variant}
            field={field}
            disabled={disabled}
            showDeleteButton={showDeleteButton}
          />
          )}
          disabled={disabled}
          minDate={utcToZonedUtility(minDate)}
          minTime={minTime}
          maxTime={maxTime}
          popperPlacement={popperPlacement}
          popperModifiers={[
            offset(calendarOffset),
          ]}
          popperContainer={
            renderInPortal
              ? ({ children }) => <Portal>{children}</Portal>
              : undefined
          }
        />
        {error && errorText && (
          <FormHelperText as="div" mt={2}>
            <Text fontSize="md" fontWeight="semibold" color="red.500">{errorText}</Text>
          </FormHelperText>
        )}
        {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
      </Box>
    </FormControl>
  )
}

type CustomInputProps = {
  variant: string;
  disabled?: boolean;
  showDeleteButton?: boolean;
  field: ControllerRenderProps<any, string>;
  onChange?: () => void;
  onBlur?: (e: React.FocusEvent) => void;
  placeholder?: string;
  value?: string;
  id?: string;
  onClick?: () => void;
}

const CustomInput = ({
  variant, disabled, showDeleteButton, field, onChange, onBlur, placeholder, value, id, onClick,
}: CustomInputProps) => (
  <InputGroup>
    <Input
      variant={variant}
      bg="white"
      color={disabled ? "gray.400" : undefined}
      onChange={onChange}
      onBlur={(e) => {
        field.onBlur()
        onBlur?.(e)
      }}
      placeholder={placeholder}
      value={value}
      id={id}
      onClick={onClick}
    />{(showDeleteButton && value) && (
    <InputRightElement w="auto"><CloseButton onClick={() => { field.onChange(null) }} />
    </InputRightElement>
    )}
  </InputGroup>
)

export default DatePickerField
